import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/ui/PageHeader';
import { ApiError } from '@/lib/api';
import { catalogApi, type CatalogMeta, type CatalogRow } from '@/lib/catalog';
import CatalogFilters, { type CatalogFilterState } from './components/CatalogFilters';
import ProductGrid from './components/ProductGrid';
import ProductTable from './components/ProductTable';

/** Каталог на данных сервера (этап 1.3): строки с себестоимостью, ценой B2C и маржой; фильтры и сортировка — как в макете. */
const initialState = (maxPrice: number): CatalogFilterState => ({
  source: 'All',
  partnerId: 'All',
  category: 'All',
  inStock: false,
  maxPrice,
  color: 'All',
  base: 'All',
  query: '',
});

const ceil25 = (v: number) => Math.max(150, Math.ceil(v / 25) * 25);

export default function CatalogPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [view, setView] = useState<'grid' | 'table'>('table');
  const [meta, setMeta] = useState<CatalogMeta | null>(null);
  const [all, setAll] = useState<CatalogRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<CatalogFilterState>(initialState(3000));
  const [sort, setSort] = useState('margin');
  const priceCap = meta ? ceil25(meta.maxPrice) : 3000;

  useEffect(() => {
    let alive = true;
    Promise.all([catalogApi.meta(), catalogApi.list({ limit: 5000 })])
      .then(([m, rows]) => {
        if (!alive) return;
        setMeta(m);
        setAll(rows);
        setFilters(initialState(ceil25(m.maxPrice)));
      })
      .catch((e) => alive && setError((e as ApiError).code));
    return () => {
      alive = false;
    };
  }, []);

  const rows = useMemo(() => {
    const list = (all ?? []).filter((p) => {
      const q = filters.query.trim().toLowerCase();
      if (filters.source !== 'All' && p.source !== filters.source) return false;
      if (filters.partnerId !== 'All' && p.partnerId !== filters.partnerId) return false;
      if (filters.category !== 'All' && p.category !== filters.category) return false;
      if (filters.inStock && p.stock === 0) return false;
      if (p.sale != null && p.sale > filters.maxPrice) return false;
      if (filters.color !== 'All' && p.color !== filters.color) return false;
      if (filters.base !== 'All' && p.base !== filters.base) return false;
      if (q && !p.sku.toLowerCase().includes(q) && !p.name.toLowerCase().includes(q) && !(p.supplierArticle || '').toLowerCase().includes(q)) return false;
      return true;
    });
    const n = (v: number | null) => v ?? -1;
    return list.sort((a, b) => {
      if (sort === 'margin') return n(b.marginPct) - n(a.marginPct);
      if (sort === 'price-low') return (a.sale ?? 1e12) - (b.sale ?? 1e12);
      if (sort === 'price-high') return n(b.sale) - n(a.sale);
      if (sort === 'stock') return b.stock - a.stock;
      return a.sku.localeCompare(b.sku);
    });
  }, [all, filters, sort]);

  const activeCount =
    (filters.source !== 'All' ? 1 : 0) +
    (filters.partnerId !== 'All' ? 1 : 0) +
    (filters.category !== 'All' ? 1 : 0) +
    (filters.inStock ? 1 : 0) +
    (filters.color !== 'All' ? 1 : 0) +
    (filters.base !== 'All' ? 1 : 0) +
    (filters.maxPrice !== priceCap ? 1 : 0);
  const reset = () => setFilters(initialState(priceCap));

  return (
    <div>
      <PageHeader
        eyebrow={t('catalog.eyebrow')}
        title={t('catalog.title')}
        description={t('catalog.description')}
        actions={
          <>
            <Link
              to="/import"
              className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-upload-cloud-2-line text-[14px]" /> {t('catalog.importPrices')}
            </Link>
            <Link
              to="/specifications"
              className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-file-list-3-line text-[14px]" /> {t('catalog.buildSpec')}
            </Link>
          </>
        }
      />

      {error && <p className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">{t('onb.error', { code: error })}</p>}

      <div className="flex flex-col gap-4 lg:flex-row">
        <CatalogFilters value={filters} onChange={setFilters} activeCount={activeCount} onReset={reset} meta={meta} priceCap={priceCap} />

        <div className="min-w-0 flex-1">
          <div className="mb-3 flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[12px] text-foreground-600">
                <span className="tnum font-semibold text-foreground-950">{rows.length}</span> {t('common.items')}
              </p>
              <span className="h-3 w-px bg-background-300" />
              <p className="text-[11.5px] text-foreground-400">{t('catalog.split', { partner: meta?.partnerCount ?? 0, own: meta?.ownCount ?? 0 })}</p>
              {activeCount > 0 && (
                <button type="button" onClick={reset} className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-[10.5px] font-medium text-primary-800">
                  {activeCount} <i className="ri-close-line text-[10px]" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                aria-label={t('catalog.colMargin')}
                className="h-7 cursor-pointer rounded border border-background-200 bg-background-50 px-2 text-[11.5px] text-foreground-700 outline-none focus:border-primary-400"
              >
                <option value="margin">{t('catalog.sortMargin')}</option>
                <option value="price-low">{t('catalog.sortPriceLow')}</option>
                <option value="price-high">{t('catalog.sortPriceHigh')}</option>
                <option value="stock">{t('catalog.sortStock')}</option>
                <option value="sku">{t('catalog.sortSku')}</option>
              </select>
              <div className="flex items-center gap-0.5 rounded border border-background-200 bg-background-100 p-0.5">
                <button type="button" onClick={() => setView('grid')} aria-label={t('catalog.grid')} className={`flex h-6 w-6 cursor-pointer items-center justify-center rounded ${view === 'grid' ? 'bg-background-50 text-foreground-900' : 'text-foreground-400 hover:text-foreground-700'}`}>
                  <i className="ri-grid-line text-[13px]" />
                </button>
                <button type="button" onClick={() => setView('table')} aria-label={t('catalog.table')} className={`flex h-6 w-6 cursor-pointer items-center justify-center rounded ${view === 'table' ? 'bg-background-50 text-foreground-900' : 'text-foreground-400 hover:text-foreground-700'}`}>
                  <i className="ri-list-check-2 text-[13px]" />
                </button>
              </div>
            </div>
          </div>

          {all === null ? (
            <div className="rounded-lg border border-background-200 bg-background-50 px-6 py-14 text-center text-[12.5px] text-foreground-500">{t('ui.loading')}</div>
          ) : rows.length === 0 ? (
            <div className="rounded-lg border border-background-200 bg-background-50 px-6 py-14 text-center">
              <i className="ri-search-eye-line text-2xl text-foreground-300" />
              <p className="mt-2 text-[13px] font-medium text-foreground-900">{t('catalog.emptyTitle')}</p>
              <p className="mt-1 text-[11.5px] text-foreground-500">{t('catalog.emptyBody')}</p>
              <button type="button" onClick={reset} className="mt-3 h-7 cursor-pointer whitespace-nowrap rounded border border-background-300 px-3 text-[11.5px] font-medium text-foreground-700 hover:bg-background-100">
                {t('catalog.resetFilters')}
              </button>
            </div>
          ) : view === 'grid' ? (
            <ProductGrid rows={rows} onOpen={(sku) => navigate(`/catalog/${encodeURIComponent(sku)}`)} />
          ) : (
            <ProductTable rows={rows} onOpen={(sku) => navigate(`/catalog/${encodeURIComponent(sku)}`)} />
          )}

          <div className="mt-3 flex flex-col gap-1 px-1 text-[10.5px] text-foreground-400 sm:flex-row sm:items-center sm:justify-between">
            <p>{t('common.vatNoteCosts')}</p>
            <p className="tnum">
              {t('common.snapshot')} · {t('catalog.footerNote', { count: meta?.activePartners ?? 0 })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
